import { Dispatch, Action, ActionCreator } from "redux";
import { ThunkAction } from "redux-thunk";

import { GlobalState } from "@/Reducers/interface";
import {
	RequestMarketplaceProductRequest,
	MarketplaceVendorSubscription,
	MarketplaceCollection
} from "@/Contracts/Marketplace";
import {
	getMarketplaceProductDetails,
	getMarketplaceProducts,
	requestMarketplaceProduct,
	getMarketplaceVendors,
	getMarketplaceSubscriptions,
	updateClientSubscriptions,
	getMarketplaceInvoices,
	getMarketplaceVendorDetails,
	getMarketplaceVendorProductDetails
} from "@/Services/Marketplace";
import { ChargebeeInvoice } from "@/Contracts/Users";
import {
	MarketplaceProduct,
	MarketplaceProductCountMap
} from "@/Contracts/Marketplace/Product";
import { VendorCompany } from "@/Contracts/Marketplace/Vendor";
import { getVendorList } from "@/Services/Marketplace/Vendor";

export enum MarketplaceActionTypes {
	SHOW_MARKETPLACE_LOADER = "SHOW_MARKETPLACE_LOADER",
	HIDE_MARKETPLACE_LOADER = "HIDE_MARKETPLACE_LOADER",
	MARKETPLACE_PRODUCTS_REQUESTED = "MARKETPLACE_PRODUCTS_REQUESTED",
	MARKETPLACE_PRODUCTS_SUCCESSFUL = "MARKETPLACE_PRODUCTS_SUCCESSFUL",
	MARKETPLACE_PRODUCTS_FAILED = "MARKETPLACE_PRODUCTS_FAILED",
	MARKETPLACE_PRODUCT_DETAILS_REQUESTED = "MARKETPLACE_PRODUCT_DETAILS_REQUESTED",
	MARKETPLACE_PRODUCT_DETAILS_SUCCESSFUL = "MARKETPLACE_PRODUCT_DETAILS_SUCCESSFUL",
	MARKETPLACE_PRODUCT_DETAILS_FAILED = "MARKETPLACE_PRODUCT_DETAILS_FAILED",
	MARKETPLACE_PRODUCT_DETAILS_RESET = "MARKETPLACE_PRODUCT_DETAILS_RESET",
	REQUEST_MARKETPLACE_PRODUCT_REQUESTED = "REQUEST_MARKETPLACE_PRODUCT_REQUESTED",
	REQUEST_MARKETPLACE_PRODUCT_SUCCESSFUL = "REQUEST_MARKETPLACE_PRODUCT_SUCCESSFUL",
	REQUEST_MARKETPLACE_PRODUCT_FAILED = "REQUEST_MARKETPLACE_PRODUCT_FAILED",
	MARKETPLACE_VENDORS_REQUESTED = "MARKETPLACE_VENDORS_REQUESTED",
	MARKETPLACE_VENDORS_SUCCESSFUL = "MARKETPLACE_VENDORS_SUCCESSFUL",
	MARKETPLACE_VENDORS_FAILED = "MARKETPLACE_VENDORS_FAILED",
	MARKETPLACE_SUBSCRIPTIONS_REQUESTED = "MARKETPLACE_SUBSCRIPTIONS_REQUESTED",
	MARKETPLACE_SUBSCRIPTIONS_SUCCESSFUL = "MARKETPLACE_SUBSCRIPTIONS_SUCCESSFUL",
	MARKETPLACE_SUBSCRIPTIONS_FAILED = "MARKETPLACE_SUBSCRIPTIONS_FAILED",
	UPDATE_CLIENT_SUBSCRIPTIONS_REQUESTED = "UPDATE_CLIENT_SUBSCRIPTIONS_REQUESTED",
	UPDATE_CLIENT_SUBSCRIPTIONS_SUCCESSFUL = "UPDATE_CLIENT_SUBSCRIPTIONS_SUCCESSFUL",
	UPDATE_CLIENT_SUBSCRIPTIONS_FAILED = "UPDATE_CLIENT_SUBSCRIPTIONS_FAILED",
	MARKETPLACE_INVOICES_REQUESTED = "MARKETPLACE_INVOICES_REQUESTED",
	MARKETPLACE_INVOICES_SUCCESSFUL = "MARKETPLACE_INVOICES_SUCCESSFUL",
	MARKETPLACE_INVOICES_FAILED = "MARKETPLACE_INVOICES_FAILED",
	MARKETPLACE_VENDOR_DETAILS_SUCCESSFUL = "MARKETPLACE_VENDOR_DETAILS_SUCCESSFUL",
	MARKETPLACE_VENDOR_DETAILS_RESET = "MARKETPLACE_VENDOR_DETAILS_RESET",
	MARKETPLACE_VENDOR_DETAILS_REQUESTED = "MARKETPLACE_VENDOR_DETAILS_REQUESTED",
	MARKETPLACE_VENDOR_DETAILS_FAILED = "MARKETPLACE_VENDOR_DETAILS_FAILED",
	MARKETPLACE_VENDOR_PRODUCT_DETAILS_SUCCESSFUL = "MARKETPLACE_VENDOR_PRODUCT_DETAILS_SUCCESSFUL",
	MARKETPLACE_VENDOR_PRODUCT_DETAILS_RESET = "MARKETPLACE_VENDOR_PRODUCT_DETAILS_RESET",
	MARKETPLACE_VENDOR_PRODUCT_DETAILS_REQUESTED = "MARKETPLACE_VENDOR_PRODUCT_DETAILS_REQUESTED",
	MARKETPLACE_VENDOR_PRODUCT_DETAILS_FAILED = "MARKETPLACE_VENDOR_PRODUCT_DETAILS_FAILED",
	SHOW_VENDOR_PROGRAM_STATUS_LOADER = "SHOW_VENDOR_PROGRAM_STATUS_LOADER",
	HIDE_VENDOR_PROGRAM_STATUS_LOADER = "HIDE_VENDOR_PROGRAM_STATUS_LOADER",
	VENDOR_PROGRAM_STATUS_REQUESTED = "VENDOR_PROGRAM_STATUS_REQUESTED",
	VENDOR_PROGRAM_STATUS_SUCCESSFUL = "VENDOR_PROGRAM_STATUS_SUCCESSFUL",
	VENDOR_PROGRAM_STATUS_FAILED = "VENDOR_PROGRAM_STATUS_FAILED"
}

export type ShowMarketplaceLoaderAction =
	Action<MarketplaceActionTypes.SHOW_MARKETPLACE_LOADER>;
export type HideMarketplaceLoaderAction =
	Action<MarketplaceActionTypes.HIDE_MARKETPLACE_LOADER>;

export type MarketplaceProductsRequestedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_PRODUCTS_REQUESTED>;
export interface MarketplaceProductsSuccessfulAction
	extends Action<MarketplaceActionTypes.MARKETPLACE_PRODUCTS_SUCCESSFUL> {
	readonly products: MarketplaceProduct[];
	readonly productCount: MarketplaceProductCountMap;
}
export type MarketplaceProductsFailedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_PRODUCTS_FAILED>;

export type MarketplaceProductDetailsRequestedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_PRODUCT_DETAILS_REQUESTED>;
export interface MarketplaceProductDetailsSuccessfulAction
	extends Action<MarketplaceActionTypes.MARKETPLACE_PRODUCT_DETAILS_SUCCESSFUL> {
	readonly currentActiveMarketplaceProduct: MarketplaceProduct;
}
export type MarketplaceProductDetailsFailedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_PRODUCT_DETAILS_FAILED>;
export type MarketplaceProductDetailsResetAction =
	Action<MarketplaceActionTypes.MARKETPLACE_PRODUCT_DETAILS_RESET>;

export type RequestMarketplaceProductRequestedAction =
	Action<MarketplaceActionTypes.REQUEST_MARKETPLACE_PRODUCT_REQUESTED>;
export type RequestMarketplaceProductSuccessfulAction =
	Action<MarketplaceActionTypes.REQUEST_MARKETPLACE_PRODUCT_SUCCESSFUL>;
export type RequestMarketplaceProductFailedAction =
	Action<MarketplaceActionTypes.REQUEST_MARKETPLACE_PRODUCT_FAILED>;

export type MarketplaceVendorsRequestedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_VENDORS_REQUESTED>;
export interface MarketplaceVendorsSuccessfulAction
	extends Action<MarketplaceActionTypes.MARKETPLACE_VENDORS_SUCCESSFUL> {
	readonly vendors: VendorCompany[];
}
export type MarketplaceVendorsFailedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_VENDORS_FAILED>;

export type MarketplaceSubscriptionsRequestedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_SUBSCRIPTIONS_REQUESTED>;
export interface MarketplaceSubscriptionSuccessfulAction
	extends Action<MarketplaceActionTypes.MARKETPLACE_SUBSCRIPTIONS_SUCCESSFUL> {
	readonly subscriptions: MarketplaceVendorSubscription[];
}

export type MarketplaceSubscriptionsFailedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_SUBSCRIPTIONS_FAILED>;

export type UpdateClientSubscriptionsRequestedAction =
	Action<MarketplaceActionTypes.UPDATE_CLIENT_SUBSCRIPTIONS_REQUESTED>;

export interface UpdateClientSubscriptionsSuccessfulAction
	extends Action<MarketplaceActionTypes.UPDATE_CLIENT_SUBSCRIPTIONS_SUCCESSFUL> {
	readonly subscriptions: MarketplaceVendorSubscription;
}

export type UpdateClientSubscriptionsFailedAction =
	Action<MarketplaceActionTypes.UPDATE_CLIENT_SUBSCRIPTIONS_FAILED>;
export type MarketplaceInvoicesRequestedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_INVOICES_REQUESTED>;
export interface MarketplaceInvoicesSuccessfulAction
	extends Action<MarketplaceActionTypes.MARKETPLACE_INVOICES_SUCCESSFUL> {
	readonly invoices: ChargebeeInvoice[];
}

export type MarketplaceInvoicesFailedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_INVOICES_FAILED>;

export type MarketplaceVendorDetailsRequestedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_VENDOR_DETAILS_REQUESTED>;
export interface MarketplaceVendorDetailsSuccessfulAction
	extends Action<MarketplaceActionTypes.MARKETPLACE_VENDOR_DETAILS_SUCCESSFUL> {
	readonly currentActiveMarketplaceVendor: VendorCompany;
}
export type MarketplaceVendorDetailsFailedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_VENDOR_DETAILS_FAILED>;
export type MarketplaceVendorDetailsResetAction =
	Action<MarketplaceActionTypes.MARKETPLACE_VENDOR_DETAILS_RESET>;
export type MarketplaceVendorProductDetailsRequestedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_VENDOR_PRODUCT_DETAILS_REQUESTED>;
export interface MarketplaceVendorProductDetailsSuccessfulAction
	extends Action<MarketplaceActionTypes.MARKETPLACE_VENDOR_PRODUCT_DETAILS_SUCCESSFUL> {
	readonly currentActiveMarketplaceVendorProducts: MarketplaceProduct[];
}
export type MarketplaceVendorProductDetailsFailedAction =
	Action<MarketplaceActionTypes.MARKETPLACE_VENDOR_PRODUCT_DETAILS_FAILED>;
export type MarketplaceVendorProductDetailsResetAction =
	Action<MarketplaceActionTypes.MARKETPLACE_VENDOR_PRODUCT_DETAILS_RESET>;

export type ShowVendorProgramStatusLoaderAction =
	Action<MarketplaceActionTypes.SHOW_VENDOR_PROGRAM_STATUS_LOADER>;
export type HideVendorProgramStatusLoaderAction =
	Action<MarketplaceActionTypes.HIDE_VENDOR_PROGRAM_STATUS_LOADER>;

export type VendorProgramStatusDataRequestedAction =
	Action<MarketplaceActionTypes.VENDOR_PROGRAM_STATUS_REQUESTED>;

export interface VendorProgramStatusDataSuccessfulAction
	extends Action<MarketplaceActionTypes.VENDOR_PROGRAM_STATUS_SUCCESSFUL> {
	readonly vendorProgamDetails: VendorCompany[];
}

export type VendorProgramStatusDataFailedAction =
	Action<MarketplaceActionTypes.VENDOR_PROGRAM_STATUS_FAILED>;

export type MarketplaceActions =
	| ShowMarketplaceLoaderAction
	| HideMarketplaceLoaderAction
	| MarketplaceProductsRequestedAction
	| MarketplaceProductsSuccessfulAction
	| MarketplaceProductsFailedAction
	| MarketplaceProductDetailsRequestedAction
	| MarketplaceProductDetailsSuccessfulAction
	| MarketplaceProductDetailsFailedAction
	| MarketplaceProductDetailsResetAction
	| RequestMarketplaceProductRequestedAction
	| RequestMarketplaceProductSuccessfulAction
	| RequestMarketplaceProductFailedAction
	| MarketplaceVendorsRequestedAction
	| MarketplaceVendorsSuccessfulAction
	| MarketplaceVendorsFailedAction
	| MarketplaceSubscriptionsRequestedAction
	| MarketplaceSubscriptionSuccessfulAction
	| MarketplaceSubscriptionsFailedAction
	| UpdateClientSubscriptionsRequestedAction
	| UpdateClientSubscriptionsSuccessfulAction
	| UpdateClientSubscriptionsFailedAction
	| MarketplaceInvoicesRequestedAction
	| MarketplaceInvoicesSuccessfulAction
	| MarketplaceInvoicesFailedAction
	| MarketplaceVendorDetailsRequestedAction
	| MarketplaceVendorDetailsSuccessfulAction
	| MarketplaceVendorDetailsFailedAction
	| MarketplaceVendorDetailsResetAction
	| MarketplaceVendorProductDetailsRequestedAction
	| MarketplaceVendorProductDetailsSuccessfulAction
	| MarketplaceVendorProductDetailsFailedAction
	| MarketplaceVendorProductDetailsResetAction
	| ShowVendorProgramStatusLoaderAction
	| HideVendorProgramStatusLoaderAction
	| VendorProgramStatusDataRequestedAction
	| VendorProgramStatusDataSuccessfulAction
	| VendorProgramStatusDataFailedAction;

export interface MarketplaceDispatch {
	showMarketplaceLoader: ActionCreator<
		ThunkAction<
			ShowMarketplaceLoaderAction,
			GlobalState,
			null,
			ShowMarketplaceLoaderAction
		>
	>;
	hideMarketplaceLoader: ActionCreator<
		ThunkAction<
			HideMarketplaceLoaderAction,
			GlobalState,
			null,
			HideMarketplaceLoaderAction
		>
	>;
	getMarketplaceProducts: ActionCreator<
		ThunkAction<
			Promise<MarketplaceProductsSuccessfulAction>,
			GlobalState,
			null,
			MarketplaceProductsSuccessfulAction
		>
	>;
	getMarketplaceProductDetails: ActionCreator<
		ThunkAction<
			Promise<MarketplaceProductDetailsSuccessfulAction>,
			GlobalState,
			string,
			MarketplaceProductDetailsSuccessfulAction
		>
	>;
	resetMarketplaceProductDetails: ActionCreator<
		ThunkAction<
			MarketplaceProductDetailsResetAction,
			GlobalState,
			null,
			MarketplaceProductDetailsResetAction
		>
	>;
	getMarketplaceVendors: ActionCreator<
		ThunkAction<
			Promise<MarketplaceVendorsSuccessfulAction>,
			GlobalState,
			null,
			MarketplaceVendorsSuccessfulAction
		>
	>;
	requestMarketplaceProduct: ActionCreator<
		ThunkAction<
			Promise<RequestMarketplaceProductSuccessfulAction>,
			GlobalState,
			RequestMarketplaceProductRequest,
			RequestMarketplaceProductSuccessfulAction
		>
	>;
	getMarketplaceSubscriptions: ActionCreator<
		ThunkAction<
			Promise<MarketplaceSubscriptionSuccessfulAction>,
			GlobalState,
			null,
			MarketplaceSubscriptionSuccessfulAction
		>
	>;
	updateClientSubscriptions: ActionCreator<
		ThunkAction<
			Promise<UpdateClientSubscriptionsSuccessfulAction>,
			GlobalState,
			null,
			UpdateClientSubscriptionsSuccessfulAction
		>
	>;
	getMarketplaceInvoices: ActionCreator<
		ThunkAction<
			Promise<MarketplaceInvoicesSuccessfulAction>,
			GlobalState,
			null,
			MarketplaceInvoicesSuccessfulAction
		>
	>;
	getMarketplaceVendorDetails: ActionCreator<
		ThunkAction<
			Promise<MarketplaceVendorDetailsSuccessfulAction>,
			GlobalState,
			string,
			MarketplaceVendorDetailsSuccessfulAction
		>
	>;
	resetMarketplaceVendorDetails: ActionCreator<
		ThunkAction<
			MarketplaceVendorDetailsResetAction,
			GlobalState,
			null,
			MarketplaceVendorDetailsResetAction
		>
	>;
	getMarketplaceVendorProductDetails: ActionCreator<
		ThunkAction<
			Promise<MarketplaceVendorProductDetailsSuccessfulAction>,
			GlobalState,
			string,
			MarketplaceVendorProductDetailsSuccessfulAction
		>
	>;
	resetMarketplaceVendorProductDetails: ActionCreator<
		ThunkAction<
			MarketplaceVendorProductDetailsResetAction,
			GlobalState,
			null,
			MarketplaceVendorProductDetailsResetAction
		>
	>;
	showVendorProgramStatusLoader: ActionCreator<
		ThunkAction<
			ShowVendorProgramStatusLoaderAction,
			GlobalState,
			null,
			ShowVendorProgramStatusLoaderAction
		>
	>;
	hideVendorProgramStatusLoader: ActionCreator<
		ThunkAction<
			HideVendorProgramStatusLoaderAction,
			GlobalState,
			null,
			HideVendorProgramStatusLoaderAction
		>
	>;

	getVendorProgramStatusData: ActionCreator<
		ThunkAction<
			Promise<VendorProgramStatusDataSuccessfulAction>,
			GlobalState,
			null,
			VendorProgramStatusDataSuccessfulAction
		>
	>;
}

export const MarketplaceActionCreators: MarketplaceDispatch = {
	showMarketplaceLoader: () => {
		return (dispatch: Dispatch): ShowMarketplaceLoaderAction => {
			const showMarketplaceLoaderAction: ShowMarketplaceLoaderAction = {
				type: MarketplaceActionTypes.SHOW_MARKETPLACE_LOADER
			};
			return dispatch(showMarketplaceLoaderAction);
		};
	},
	hideMarketplaceLoader: () => {
		return (dispatch: Dispatch): HideMarketplaceLoaderAction => {
			const hideMarketplaceLoaderAction: HideMarketplaceLoaderAction = {
				type: MarketplaceActionTypes.HIDE_MARKETPLACE_LOADER
			};
			return dispatch(hideMarketplaceLoaderAction);
		};
	},
	getMarketplaceProducts: (collection?: MarketplaceCollection) => {
		return async (
			dispatch: Dispatch
		): Promise<MarketplaceProductsSuccessfulAction> => {
			const marketplaceProductsRequestedAction: MarketplaceProductsRequestedAction =
				{
					type: MarketplaceActionTypes.MARKETPLACE_PRODUCTS_REQUESTED
				};
			dispatch(marketplaceProductsRequestedAction);
			try {
				const marketplaceProductsResponse =
					await getMarketplaceProducts(collection);
				const marketplaceProductsSuccessfulAction: MarketplaceProductsSuccessfulAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_PRODUCTS_SUCCESSFUL,
						products: marketplaceProductsResponse.data,
						productCount:
							marketplaceProductsResponse.filtered_count_map
					};
				return dispatch(marketplaceProductsSuccessfulAction);
			} catch (error) {
				const MarketplaceProductsFailedAction: MarketplaceProductsFailedAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_PRODUCTS_FAILED
					};
				dispatch(MarketplaceProductsFailedAction);
				return Promise.reject(error);
			}
		};
	},
	getMarketplaceProductDetails: (marketplaceProductId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<MarketplaceProductDetailsSuccessfulAction> => {
			const marketplaceProductDetailsRequestedAction: MarketplaceProductDetailsRequestedAction =
				{
					type: MarketplaceActionTypes.MARKETPLACE_PRODUCT_DETAILS_REQUESTED
				};
			dispatch(marketplaceProductDetailsRequestedAction);
			try {
				const marketplaceProductDetailsResponse =
					await getMarketplaceProductDetails(marketplaceProductId);
				const marketplaceProductDetailsSuccessfulAction: MarketplaceProductDetailsSuccessfulAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_PRODUCT_DETAILS_SUCCESSFUL,
						currentActiveMarketplaceProduct:
							marketplaceProductDetailsResponse
					};
				return dispatch(marketplaceProductDetailsSuccessfulAction);
			} catch (error) {
				const marketplaceProductDetailsFailedAction: MarketplaceProductDetailsFailedAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_PRODUCT_DETAILS_FAILED
					};
				dispatch(marketplaceProductDetailsFailedAction);
				return Promise.reject(error);
			}
		};
	},
	resetMarketplaceProductDetails: () => {
		return (dispatch: Dispatch): MarketplaceProductDetailsResetAction => {
			const marketplaceProductDetailsResetAction: MarketplaceProductDetailsResetAction =
				{
					type: MarketplaceActionTypes.MARKETPLACE_PRODUCT_DETAILS_RESET
				};
			return dispatch(marketplaceProductDetailsResetAction);
		};
	},
	getMarketplaceVendors: () => {
		return async (
			dispatch: Dispatch
		): Promise<MarketplaceVendorsSuccessfulAction> => {
			const marketplaceVendorsRequestedAction: MarketplaceVendorsRequestedAction =
				{
					type: MarketplaceActionTypes.MARKETPLACE_VENDORS_REQUESTED
				};
			dispatch(marketplaceVendorsRequestedAction);
			try {
				const marketplaceVendorsResponse =
					await getMarketplaceVendors();
				const marketplaceVendorsSuccessfulAction: MarketplaceVendorsSuccessfulAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_VENDORS_SUCCESSFUL,
						vendors: marketplaceVendorsResponse.data
					};
				return dispatch(marketplaceVendorsSuccessfulAction);
			} catch (error) {
				const marketplaceVendorsFailedAction: MarketplaceVendorsFailedAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_VENDORS_FAILED
					};
				dispatch(marketplaceVendorsFailedAction);
				return Promise.reject(error);
			}
		};
	},
	requestMarketplaceProduct: (
		marketplaceProductId: string,
		requestMarketplaceProductRequest: RequestMarketplaceProductRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<RequestMarketplaceProductSuccessfulAction> => {
			const requestMarketplaceProductRequestedAction: RequestMarketplaceProductRequestedAction =
				{
					type: MarketplaceActionTypes.REQUEST_MARKETPLACE_PRODUCT_REQUESTED
				};
			dispatch(requestMarketplaceProductRequestedAction);
			try {
				await requestMarketplaceProduct(
					marketplaceProductId,
					requestMarketplaceProductRequest
				);
				const requestMarketplaceProductSuccessfulAction: RequestMarketplaceProductSuccessfulAction =
					{
						type: MarketplaceActionTypes.REQUEST_MARKETPLACE_PRODUCT_SUCCESSFUL
					};
				return dispatch(requestMarketplaceProductSuccessfulAction);
			} catch (error) {
				const requestMarketplaceProductFailedAction: RequestMarketplaceProductFailedAction =
					{
						type: MarketplaceActionTypes.REQUEST_MARKETPLACE_PRODUCT_FAILED
					};
				dispatch(requestMarketplaceProductFailedAction);
				return Promise.reject(error);
			}
		};
	},
	getMarketplaceSubscriptions: () => {
		return async (
			dispatch: Dispatch
		): Promise<MarketplaceSubscriptionSuccessfulAction> => {
			const marketplaceSubscriptionsRequestedAction: MarketplaceSubscriptionsRequestedAction =
				{
					type: MarketplaceActionTypes.MARKETPLACE_SUBSCRIPTIONS_REQUESTED
				};
			dispatch(marketplaceSubscriptionsRequestedAction);
			try {
				const marketplaceSubscriptionResponse =
					await getMarketplaceSubscriptions();
				const marketplaceSubscriptionSuccessfulAction: MarketplaceSubscriptionSuccessfulAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_SUBSCRIPTIONS_SUCCESSFUL,
						subscriptions: marketplaceSubscriptionResponse.data
					};
				return dispatch(marketplaceSubscriptionSuccessfulAction);
			} catch (error) {
				const MarketplaceSubscriptionFailedAction: MarketplaceSubscriptionsFailedAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_SUBSCRIPTIONS_FAILED
					};
				dispatch(MarketplaceSubscriptionFailedAction);
				return Promise.reject(error);
			}
		};
	},
	getMarketplaceInvoices: () => {
		return async (
			dispatch: Dispatch
		): Promise<MarketplaceInvoicesSuccessfulAction> => {
			const marketplaceInvoiceRequestedAction: MarketplaceInvoicesRequestedAction =
				{
					type: MarketplaceActionTypes.MARKETPLACE_INVOICES_REQUESTED
				};
			dispatch(marketplaceInvoiceRequestedAction);
			try {
				const marketplaceInvoiceResponse =
					await getMarketplaceInvoices();
				const marketplaceInvoiceSuccessfulAction: MarketplaceInvoicesSuccessfulAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_INVOICES_SUCCESSFUL,
						invoices: marketplaceInvoiceResponse.data
					};
				return dispatch(marketplaceInvoiceSuccessfulAction);
			} catch (error) {
				const marketplaceInvoiceFailedAction: MarketplaceInvoicesFailedAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_INVOICES_FAILED
					};
				dispatch(marketplaceInvoiceFailedAction);
				return Promise.reject(error);
			}
		};
	},
	updateClientSubscriptions: (clientId: string, subscriptionId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<UpdateClientSubscriptionsSuccessfulAction> => {
			const updateClientSubscriptionsRequestedAction: UpdateClientSubscriptionsRequestedAction =
				{
					type: MarketplaceActionTypes.UPDATE_CLIENT_SUBSCRIPTIONS_REQUESTED
				};
			dispatch(updateClientSubscriptionsRequestedAction);
			try {
				const subscriptionResponse = await updateClientSubscriptions(
					clientId,
					subscriptionId
				);
				const updateClientSubscriptionSuccessfulAction: UpdateClientSubscriptionsSuccessfulAction =
					{
						type: MarketplaceActionTypes.UPDATE_CLIENT_SUBSCRIPTIONS_SUCCESSFUL,
						subscriptions: subscriptionResponse
					};
				return dispatch(updateClientSubscriptionSuccessfulAction);
			} catch (error) {
				const updateClientSubscriptionsFailedAction: UpdateClientSubscriptionsFailedAction =
					{
						type: MarketplaceActionTypes.UPDATE_CLIENT_SUBSCRIPTIONS_FAILED
					};
				dispatch(updateClientSubscriptionsFailedAction);
				return Promise.reject(error);
			}
		};
	},
	getMarketplaceVendorDetails: (marketplaceVendorId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<MarketplaceVendorDetailsSuccessfulAction> => {
			const marketplaceVendorDetailsRequestedAction: MarketplaceVendorDetailsRequestedAction =
				{
					type: MarketplaceActionTypes.MARKETPLACE_VENDOR_DETAILS_REQUESTED
				};
			dispatch(marketplaceVendorDetailsRequestedAction);
			try {
				const marketplaceVendorDetailsResponse =
					await getMarketplaceVendorDetails(marketplaceVendorId);
				const marketplaceVendorDetailsSuccessfulAction: MarketplaceVendorDetailsSuccessfulAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_VENDOR_DETAILS_SUCCESSFUL,
						currentActiveMarketplaceVendor:
							marketplaceVendorDetailsResponse
					};
				return dispatch(marketplaceVendorDetailsSuccessfulAction);
			} catch (error) {
				const marketplaceVendorDetailsFailedAction: MarketplaceVendorDetailsFailedAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_VENDOR_DETAILS_FAILED
					};
				dispatch(marketplaceVendorDetailsFailedAction);
				return Promise.reject(error);
			}
		};
	},
	resetMarketplaceVendorDetails: () => {
		return (dispatch: Dispatch): MarketplaceVendorDetailsResetAction => {
			const marketplaceVendorDetailsResetAction: MarketplaceVendorDetailsResetAction =
				{
					type: MarketplaceActionTypes.MARKETPLACE_VENDOR_DETAILS_RESET
				};
			return dispatch(marketplaceVendorDetailsResetAction);
		};
	},
	getMarketplaceVendorProductDetails: (marketplaceVendorId: string) => {
		return async (
			dispatch: Dispatch
		): Promise<MarketplaceVendorProductDetailsSuccessfulAction> => {
			const marketplaceVendorProductDetailsRequestedAction: MarketplaceVendorProductDetailsRequestedAction =
				{
					type: MarketplaceActionTypes.MARKETPLACE_VENDOR_PRODUCT_DETAILS_REQUESTED
				};
			dispatch(marketplaceVendorProductDetailsRequestedAction);
			try {
				const marketplaceVendorProductDetailsResponse =
					await getMarketplaceVendorProductDetails(
						marketplaceVendorId
					);
				const marketplaceVendorProductDetailsSuccessfulAction: MarketplaceVendorProductDetailsSuccessfulAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_VENDOR_PRODUCT_DETAILS_SUCCESSFUL,
						currentActiveMarketplaceVendorProducts:
							marketplaceVendorProductDetailsResponse.data
					};
				return dispatch(
					marketplaceVendorProductDetailsSuccessfulAction
				);
			} catch (error) {
				const marketplaceVendorProductDetailsFailedAction: MarketplaceVendorProductDetailsFailedAction =
					{
						type: MarketplaceActionTypes.MARKETPLACE_VENDOR_PRODUCT_DETAILS_FAILED
					};
				dispatch(marketplaceVendorProductDetailsFailedAction);
				return Promise.reject(error);
			}
		};
	},
	resetMarketplaceVendorProductDetails: () => {
		return (
			dispatch: Dispatch
		): MarketplaceVendorProductDetailsResetAction => {
			const marketplaceVendorProductDetailsResetAction: MarketplaceVendorProductDetailsResetAction =
				{
					type: MarketplaceActionTypes.MARKETPLACE_VENDOR_PRODUCT_DETAILS_RESET
				};
			return dispatch(marketplaceVendorProductDetailsResetAction);
		};
	},
	showVendorProgramStatusLoader: () => {
		return (dispatch: Dispatch): ShowVendorProgramStatusLoaderAction => {
			const showClientsLoaderAction: ShowVendorProgramStatusLoaderAction =
				{
					type: MarketplaceActionTypes.SHOW_VENDOR_PROGRAM_STATUS_LOADER
				};
			return dispatch(showClientsLoaderAction);
		};
	},
	hideVendorProgramStatusLoader: () => {
		return (dispatch: Dispatch): HideVendorProgramStatusLoaderAction => {
			const hideClientsLoaderAction: HideVendorProgramStatusLoaderAction =
				{
					type: MarketplaceActionTypes.HIDE_VENDOR_PROGRAM_STATUS_LOADER
				};
			return dispatch(hideClientsLoaderAction);
		};
	},
	getVendorProgramStatusData: () => {
		return async (
			dispatch: Dispatch
		): Promise<VendorProgramStatusDataSuccessfulAction> => {
			const vendorDataRequestedAction: VendorProgramStatusDataRequestedAction =
				{
					type: MarketplaceActionTypes.VENDOR_PROGRAM_STATUS_REQUESTED
				};
			dispatch(vendorDataRequestedAction);
			try {
				const vendorDataResponse = await getVendorList();
				const vendorDataSuccessfulAction: VendorProgramStatusDataSuccessfulAction =
					{
						type: MarketplaceActionTypes.VENDOR_PROGRAM_STATUS_SUCCESSFUL,
						vendorProgamDetails: vendorDataResponse.data
					};
				return dispatch(vendorDataSuccessfulAction);
			} catch (error) {
				const userDataFailedAction: VendorProgramStatusDataFailedAction =
					{
						type: MarketplaceActionTypes.VENDOR_PROGRAM_STATUS_FAILED
					};
				dispatch(userDataFailedAction);

				return Promise.reject(error);
			}
		};
	}
};

export default MarketplaceActionCreators;
